// Названия кодов найма по-русски. Источник — contracts/enums/statuses.yaml, разделы найма.
//
// **Здесь только то, чему сервер названия НЕ даёт.** Записи чёрного списка, диалоги,
// сообщения, вакансии и формулировки библиотеки приезжают с готовым `*_title` из контракта —
// его и показываем. Копия названия рядом с присланным означала бы два словаря на одно
// состояние, и через месяц они разойдутся на одну букву, а заметит это заказчик.
//
// Восемь этапов воронки — набор заказчика, подтверждён 29 июля 2026. Своих значений сюда не
// добавляем ни при каких обстоятельствах: по этим словам он считает воронку у себя и будет
// сверять их глазами на приёмке.

import type {
  CandidateStatus,
  CheckResult,
  DuplicateReviewStatus,
  RejectionReason,
} from './api/candidates';
import type { BlacklistReason, DraftVerdict, ImportStatus, MessengerGroup } from './api/blacklist';
import type {
  AssistantMode,
  AssistantSpeechMode,
  DialogStatus,
  LibraryStatus,
} from './api/dialogs';

export const CANDIDATE_STATUS_TITLES: Readonly<Record<CandidateStatus, string>> = {
  new: 'Новый',
  approved: 'Согласован',
  rejected: 'Не согласован',
  waitlist: 'Лист ожидания',
  ticket_bought: 'Куплен билет',
  no_show: 'Не прибыл',
  blacklisted: 'Чёрный список',
  hired: 'Оформлен',
};

/** Цвет метки этапа. Цвет ДУБЛИРУЕТ слово, а не заменяет его: слово читается всегда. */
export const CANDIDATE_STATUS_COLORS: Readonly<
  Record<CandidateStatus, 'gray' | 'blue' | 'green' | 'orange' | 'red' | 'purple'>
> = {
  new: 'blue',
  approved: 'green',
  rejected: 'gray',
  waitlist: 'orange',
  ticket_bought: 'purple',
  no_show: 'orange',
  blacklisted: 'red',
  hired: 'green',
};

export function candidateStatusTitle(code: string): string {
  return CANDIDATE_STATUS_TITLES[code as CandidateStatus] ?? code;
}

export const REJECTION_REASON_TITLES: Readonly<Record<RejectionReason, string>> = {
  auto_screened: 'Отсеян автоматически',
  not_qualified: 'Не подошёл по требованиям',
  declined_offer: 'Отказался сам',
  unreachable: 'Не выходит на связь',
  blacklist_match: 'Совпадение с чёрным списком',
  other: 'Иное',
};

export function rejectionReasonTitle(code: string): string {
  return REJECTION_REASON_TITLES[code as RejectionReason] ?? code;
}

/**
 * Итог сверки по чёрному списку. Три исхода, и они не сводятся к «да/нет»: точное совпадение
 * блокирует продвижение, возможное требует подтверждения на оформлении (тз 5.8.2).
 */
export const CHECK_RESULT_TITLES: Readonly<Record<CheckResult, string>> = {
  clear: 'Чисто',
  exact: 'Точное совпадение',
  possible: 'Возможное совпадение',
};

export const CHECK_RESULT_COLORS: Readonly<Record<CheckResult, 'green' | 'red' | 'orange'>> = {
  clear: 'green',
  exact: 'red',
  possible: 'orange',
};

export function checkResultTitle(code: string): string {
  return CHECK_RESULT_TITLES[code as CheckResult] ?? code;
}

export const BLACKLIST_REASON_TITLES: Readonly<Record<BlacklistReason, string>> = {
  theft: 'Кража',
  intoxication: 'Появление в состоянии опьянения',
  absence: 'Невыход',
  gross_violation: 'Грубое нарушение',
  client_conflict: 'Конфликт с заказчиком',
  other: 'Иное',
};

export const DRAFT_VERDICT_TITLES: Readonly<Record<DraftVerdict, string>> = {
  pending: 'Ждёт решения',
  to_blacklist: 'В чёрный список',
  note_only: 'Отметка о нарушении',
  discarded: 'Отброшено',
};

export const DRAFT_VERDICT_COLORS: Readonly<
  Record<DraftVerdict, 'orange' | 'red' | 'blue' | 'gray'>
> = {
  pending: 'orange',
  to_blacklist: 'red',
  note_only: 'blue',
  discarded: 'gray',
};

export const MESSENGER_GROUP_TITLES: Readonly<Record<MessengerGroup, string>> = {
  violators: 'Нарушители',
  candidates: 'Кандидаты',
};

export const IMPORT_STATUS_TITLES: Readonly<Record<ImportStatus, string>> = {
  uploaded: 'Загружена, не разобрана',
  parsed: 'Разобрана',
  reviewed: 'Решения приняты',
  failed: 'Разбор не удался',
};

export const DUPLICATE_STATUS_TITLES: Readonly<Record<DuplicateReviewStatus, string>> = {
  pending: 'Ждёт решения',
  merged: 'Склеено',
  separate: 'Разные люди',
};

/**
 * Состояния переписки. Сервер присылает `status_title` и в списке, и в журнале — этот словарь
 * нужен только там, где состояние выбирают в ОТБОРЕ: сервер названий для несуществующих ещё
 * строк не отдаёт, а подписать выпадающий список чем-то надо.
 */
export const DIALOG_STATUS_TITLES: Readonly<Record<DialogStatus, string>> = {
  awaiting_first_reply: 'Ожидает первого ответа',
  assistant: 'Ведёт помощник',
  waiting_operator: 'Ждёт оператора',
  operator: 'Ведёт оператор',
  paused: 'Пауза',
  finished: 'Завершён',
};

export const ASSISTANT_MODE_TITLES: Readonly<Record<AssistantMode, string>> = {
  off: 'Выключен',
  suggest: 'Предлагает ответ',
  auto: 'Отвечает сам',
};

/** Что режим означает на деле. Название без этого не отвечает на вопрос «а что изменится». */
export const ASSISTANT_MODE_NOTES: Readonly<Record<AssistantMode, string>> = {
  off: 'Помощник не отвечает вовсе: вся переписка по этой записи ведётся руками.',
  suggest: 'Помощник готовит ответ, наружу он уходит только после подтверждения рекрутёром.',
  auto: 'Помощник отвечает сам. Спорное и вопросы вне сценария он всё равно передаёт человеку.',
};

export const ASSISTANT_SPEECH_TITLES: Readonly<Record<AssistantSpeechMode, string>> = {
  library: 'Только утверждённые фразы',
  free: 'Своими словами',
};

/** Чем два способа речи отличаются для человека на том конце переписки. */
export const ASSISTANT_SPEECH_NOTES: Readonly<Record<AssistantSpeechMode, string>> = {
  library:
    'Помощник отправляет только те формулировки, что вы утвердили. Ноль выдумок, но всё, чего в библиотеке нет, обрывает разговор передачей человеку.',
  free: 'Помощник говорит своими словами. Проверки те же: суммы только из вилки, запретные темы не проходят, один вопрос в сообщении. Чего не знает — спросит у финансового директора и скажет соискателю, что ответит через пару минут.',
};

export const LIBRARY_STATUS_TITLES: Readonly<Record<LibraryStatus, string>> = {
  draft: 'Черновик',
  approved: 'Утверждена',
  retired: 'Выведена из обращения',
};

/** Направление обращения в журнале обмена с площадкой. */
export const EXCHANGE_DIRECTION_TITLES: Readonly<Record<'in' | 'out', string>> = {
  out: 'мы к площадке',
  in: 'площадка к нам',
};

// ── Формы для подписи над списком (правило 2) ─────────────────────────────────────────────

export const CANDIDATE_FORMS = ['кандидат', 'кандидата', 'кандидатов'] as const;
export const ENTRY_FORMS = ['запись', 'записи', 'записей'] as const;
export const DRAFT_FORMS = ['предложение', 'предложения', 'предложений'] as const;
export const CHECK_FORMS = ['сверка', 'сверки', 'сверок'] as const;
export const ACCOUNT_FORMS = ['учётная запись', 'учётные записи', 'учётных записей'] as const;
export const DIALOG_FORMS = ['переписка', 'переписки', 'переписок'] as const;
export const PHRASE_FORMS = ['формулировка', 'формулировки', 'формулировок'] as const;
export const VACANCY_FORMS = ['вакансия', 'вакансии', 'вакансий'] as const;
